'use client';

/**
 * Compteur animé : incrémente de 0 → `value` lorsqu'il entre dans le viewport.
 * Respecte `prefers-reduced-motion` (affiche directement la valeur finale).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

interface CounterProps {
  value: number;
  /** Durée de l'animation (ms). */
  duration?: number;
  decimals?: number;
  prefix?: string;
  suffix?: string;
  /** Séparateur de milliers (défaut : espace fine insécable). */
  separator?: string;
  className?: string;
}

function formatNumber(n: number, decimals: number, separator: string): string {
  const fixed = n.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const withSep = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  return decPart ? `${withSep},${decPart}` : withSep;
}

export function Counter({
  value,
  duration = 1800,
  decimals = 0,
  prefix = '',
  suffix = '',
  separator = ' ',
  className,
}: CounterProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      setDisplay(value);
      return;
    }

    let raf = 0;
    let start: number | null = null;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const tick = (now: number) => {
      if (start === null) start = now;
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(value * ease(progress));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, value, duration]);

  return (
    <span data-component-id="src/components/marketing/motion/Counter.tsx:66:4" data-component-path="src/components/marketing/motion/Counter.tsx" data-component-line="66" data-component-end-line="70" data-component-file="Counter.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" ref={ref} className={className}>
      {prefix}
      {formatNumber(display, decimals, separator)}
      {suffix}
    </span>
  );
}
